import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { generatePath, useLocation, useNavigate, useParams } from "react-router";
import { FileTypeIcon } from "../Components/FileTypeIcon";
import { FormatData } from "utils/FormatData";
import { dayjs } from "Config/dayjs";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { documentsTableStore, DocumentsTableItem } from "../Stores/DocumentsTableStore";
import { Row, message, Tooltip } from "antd";
import { Button } from "Components/General/Button";
import { DocumentsActionsMenu } from "../Components/DocumentsActionsMenu";
import { DocumentsBreadcrumbs } from "../Components/DocumentsBreadcrumbs";
import { DocumentsSidebar } from "../Components/DocumentsSidebar";
import { breadcrumbsStore } from "../Stores/BreadcrumbsStore";
import { ToolsMenu } from "../Components/ToolsMenu";
import { CheckboxProps } from "antd/lib/checkbox";
import classnames from "classnames";
import { getUrlParams } from "utils/getUrlParams";
import { DeletedFilesSwitcher } from "../Components/DeletedFilesSwitcher";
import { DocumentsSearchbar } from "../Components/Search/DocumentsSearchbar";
import { DocumentsTableRowActions } from "../Components/DocumentsTableRowActions";
import { projectsDataStore } from "Stores";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { documentsSearchStore } from "../Stores/DocumentsSearchStore";
import { FolderTypeIcon } from "../Components/FolderTypeIcon";
import { FilePreview } from "Components/FileDetails/Components/FilePreview";
import { DirectDownload } from "../Components/DirectDownload";
import { MoveFilesFormProps, MoveFilesForm } from "../Components/MoveFilesForm";
import { modalProps } from "Pages/Project/QA/utils/modalProps";
import { withModal } from "Components/General/Modal/withModal";
import { MoveFolderForm } from "../Components/MoveFolderForm";
import { LabelsPreview } from "Components/FileDetails/Components/LabelsPreview";
import { fileDetailsStore } from "Components/FileDetails/FileDetailsStore";
import { isEqual, isEmpty } from "lodash";
import { ViewLinkedMailModal } from "Pages/Project/Communication/Pages/ViewLinkedMailModal";
import { emailLinkedViewStore } from "Pages/Project/Communication/Stores/EmailLinkedViewStore";
import { ScrollableTable, TableColumnProps } from "Components/ScrollableTable/ScrollableTable";
import { DocumentsDrop } from "./DocumentsDrop";
import { I18N_NS } from "Config/i18n";
import { usePrevious } from "utils/hooks/usePrevious";
import { runMcpDocumentDownload } from "../mcpDocumentDownload";
import { searchWithoutMcpDownload, takeMcpDownload } from "../mcpDocumentDownloadQuery";
import { fileIdFromRowKey, findFileById, getDocumentsRowKey, isFileItem, isFolderItem } from "../utils/documentsItems";

const RwmRightNotify = ({ visible }) => {
  const { t } = useTranslation(I18N_NS.PROJECT);
  if (visible) {
    return (
      <Tooltip
        title={
          <>
            <span>
              {t(
                `Only read with watermark access rights on this folder: You will only be able to view or download watermark-ready files. They are marked in the file type column with a`
              )}
            </span>
            <FontAwesomeIcon style={{ marginLeft: 5 }} icon={["fas", "tint"]} color="#ff8318" fixedWidth={true} />
          </>
        }
      >
        <span>
          <FontAwesomeIcon
            color="#ff8318"
            className="mr-md pointer"
            icon={["far", "exclamation-circle"]}
            fixedWidth={true}
            size="lg"
          />
        </span>
      </Tooltip>
    );
  }

  return null;
};

interface DocumentsPageParams extends Record<string, string | undefined> {
  projectId: string;
  folderId?: string;
}

export const DocumentsPage = withModal(
  observer(({ showModal }) => {
    const { t } = useTranslation(I18N_NS.PROJECT);
    const params = useParams<DocumentsPageParams>();
    const location = useLocation();
    const navigate = useNavigate();
    const tableRef = useRef<ScrollableTable<DocumentsTableItem>>(null);
    const [selectedFileKeys, setSelectedFileKeys] = useState<string[]>([]);
    const [highlightedRowId, setHighlightedRowId] = useState<string[]>([]);
    const reinitialized = useRef(false);
    const prevFolderId = usePrevious(params.folderId);
    const prevSearch = usePrevious(location.search);
    const prevHighlightedRowId = usePrevious(highlightedRowId);

    const currentHighlightedRows = useMemo(() => {
      if (fileDetailsStore.fileId) {
        const record = findFileById(documentsTableStore.data, fileDetailsStore.fileId);
        return record ? [record._rowKey ?? getDocumentsRowKey(record)] : [];
      } else {
        return highlightedRowId;
      }
    }, [highlightedRowId, fileDetailsStore.fileId, documentsTableStore.data]);

    const initialize = useCallback(async () => {
      try {
        documentsSearchStore.reset();
        const { linkid } = getUrlParams(location.search);
        if (params.folderId) {
          await documentsTableStore.initData(+params.folderId, +linkid);
        } else {
          await documentsTableStore.initData();
        }
      } catch (error) {
        console.error("Initialization failed:", error);
        throw error;
      }
    }, [location.search, params.folderId]);

    useEffect(() => {
      const lastFolderId = documentsTableStore.getLastFolderId();
      const lastLinkId = documentsTableStore.getLastFolderLinkId();
      const urlParams = getUrlParams(location.search);
      const queryFiles =
        urlParams.file
          ?.split(",")
          .map(Number)
          .filter((id) => !isNaN(id)) || [];
      const mcpDownload = takeMcpDownload(location.search);

      if (queryFiles.length > 0) {
        setHighlightedRowId(queryFiles.map((id) => getDocumentsRowKey({ id, type: "file" })));
      }

      if (mcpDownload) {
        navigate({ pathname: location.pathname, search: searchWithoutMcpDownload(location.search) }, { replace: true });
        void runMcpDocumentDownload(mcpDownload).catch(() => {
          message.error(t("An error occurred"));
        });
      }

      if (lastFolderId && !params.folderId && !urlParams.file && !mcpDownload) {
        let uri = generatePath("/project/:projectId/documents/docs/:folderId", {
          projectId: projectsDataStore.projectId.toString(),
          folderId: lastFolderId,
        });
        if (lastLinkId) {
          uri += "?linkid=" + lastLinkId;
        }
        navigate(uri, { state: { reinitialize: true }, replace: true });
      } else {
        initialize();
      }
    }, []);

    useEffect(() => {
      const urlParams = getUrlParams(location.search);
      const prevUrlParams = getUrlParams(prevSearch ?? "");

      if (location.state && (location.state as { reinitialize: boolean }).reinitialize && !reinitialized.current) {
        initialize();
        reinitialized.current = true;
        return;
      }
      if (params.folderId !== prevFolderId) {
        (async () => {
          setSelectedFileKeys([]);
          setHighlightedRowId([]);
          documentsTableStore.clearFilters();
          tableRef.current?.resetFilters();
          tableRef.current?.setSortBy("name");
          tableRef.current?.setSortDirection("ASC");
          await documentsTableStore.fetchDifferentFolder(+params.folderId, +urlParams.linkid);
        })();
      }

      if (location.search?.length) {
        const files =
          urlParams.file
            ?.split(",")
            .map(Number)
            .filter((id) => !isNaN(id)) || [];
        const prevFiles =
          prevUrlParams.file
            ?.split(",")
            .map(Number)
            .filter((id) => !isNaN(id)) || [];
        if (!isEqual(files, prevFiles)) {
          setHighlightedRowId(files.map((id) => getDocumentsRowKey({ id, type: "file" })));
        }
      }

      if (!isEqual(currentHighlightedRows, prevHighlightedRowId ?? [])) {
        setHighlightedRowId(currentHighlightedRows);
      }
    }, [location.search, location.state, params.folderId, initialize, highlightedRowId, currentHighlightedRows]);

    const handleSort = useCallback(
      (sortBy, sortOrder) => {
        const shallReFetch = sortBy !== documentsTableStore.sortBy || sortOrder !== documentsTableStore.sortOrder;
        if (shallReFetch) {
          setHighlightedRowId([]);
          if (shallReFetch) {
            const searchParams = getUrlParams(location.search);
            if (searchParams.file) {
              navigate(location.pathname);
            }
            documentsTableStore.fetchSortedBy({
              sortBy: sortBy as keyof API_FileDataEntity,
              sortOrder,
            });
          }
        }
      },
      [location.search, location.pathname, navigate, documentsTableStore.sortBy, documentsTableStore.sortOrder]
    );

    const handleFilter = useCallback(
      (filters) => {
        if (isEmpty(filters)) {
          documentsTableStore.fetchFiltered(filters || {});
        }
        setHighlightedRowId([]);
        if (getUrlParams(location.search).file) {
          navigate(location.pathname);
        }
        documentsTableStore.fetchFiltered(filters || {});
      },
      [location.search, location.pathname, navigate]
    );

    const handleSelectRows = useCallback(({ selectedRowKeys }) => {
      setSelectedFileKeys(selectedRowKeys);
    }, []);

    const resetSelection = useCallback(() => {
      setSelectedFileKeys([]);
      setHighlightedRowId([]);
    }, []);

    const handleRowClick = useCallback(
      ({ rowData }: { rowData: DocumentsTableItem }) => {
        const { id: rowId, linkId, type, folderAccess, state } = rowData;
        const extLinkId = state === "EXTERN_SYMLINK" ? linkId : undefined;
        if (type === "FOLDER") {
          if (folderAccess === "DENIED") {
            message.error(t(`You do not have access to this directory.`));
          } else {
            setHighlightedRowId([]);
            let uri = `/project/${projectsDataStore.projectId}/documents/docs/${rowData.id}`;
            if (linkId) {
              uri += `?linkid=${linkId}`;
            }
            navigate(uri);
          }
        } else if (highlightedRowId.includes(rowData._rowKey)) {
          documentsTableStore.openFileDetails(rowId, "documents", extLinkId);
        } else {
          setHighlightedRowId([rowData._rowKey ?? getDocumentsRowKey(rowData)]);
          documentsTableStore.openFileDetails(rowId, "documents", extLinkId);
        }
      },
      [navigate, highlightedRowId]
    );

    const handleDrop = useCallback(
      ({ dropRowData, dragRowData }) => {
        if (!dropRowData.dropTarget) {
          return;
        }
        if (dropRowData?.type.indexOf("FO") === 0) {
          if (dropRowData.id === dragRowData.id) {
            return;
          }
          if (dragRowData.folderMove) {
            showModal(MoveFolderForm, {
              modalProps: { footer: null, title: t("Move directory") },
              componentProps: {
                folderId: dragRowData.id,
                stayInCurrentFolder: true,
                initialFolderId: dropRowData.id,
                onSubmitSuccess: documentsTableStore.refresh,
              },
            });
          } else if (dragRowData.fileMove) {
            showModal<Partial<MoveFilesFormProps>>(MoveFilesForm, {
              modalProps: { ...modalProps, title: t("Move files") },
              componentProps: {
                files: [dragRowData],
                defaultFolderId: dropRowData.id,
                onSubmitSuccess: documentsTableStore.goToFolder,
                onMoveFile: documentsTableStore.moveFiles,
              },
            });
          } else {
            message.error(t("You do not have access to move files."));
          }
        }
      },
      [showModal]
    );

    const getCheckboxProps = useCallback((rowData: DocumentsTableItem): CheckboxProps => {
      return isFolderItem(rowData) ? { disabled: true, checked: false, style: { display: "none" } } : {};
    }, []);

    const onCheckAllPredicate = useCallback((rowData: DocumentsTableItem): boolean => {
      return !isFolderItem(rowData);
    }, []);

    const getRowClassName = useCallback(({ linkId, state, unread, created, deleted }: DocumentsTableItem) => {
      return state || linkId
        ? classnames({
            disabledCell: deleted || state === "DELETED",
            unread: unread,
            emptyFolder: !created,
          })
        : undefined;
    }, []);

    const goToLinkedMail = useCallback((linkedMailId: number) => {
      if (linkedMailId) {
        emailLinkedViewStore.showLinkedMail("" + linkedMailId);
      }
    }, []);

    const onShowDeletedChange = useCallback(async () => {
      const nonFolderItems = documentsTableStore.data.filter((item) => !isFolderItem(item));
      const wasSelectedAllVisible = nonFolderItems.length === selectedFileKeys.length;
      await documentsTableStore.toggleShowDeleted();

      // select all files if all visible files were selected
      // and the deleted files are now shown
      if (wasSelectedAllVisible) {
        const newNonFolderKeys = documentsTableStore.data.reduce<string[]>((acc, item) => {
          if (!isFolderItem(item)) {
            acc.push(item._rowKey ?? getDocumentsRowKey(item));
          }
          return acc;
        }, []);
        setSelectedFileKeys(newNonFolderKeys);
      } else if (!documentsTableStore.showDeleted) {
        // deselect deleted files if deleted files are not shown
        const newSelectedFileKeys = selectedFileKeys.filter((key) => {
          const id = fileIdFromRowKey(key);
          const item = id != null ? findFileById(documentsTableStore.data, id) : undefined;
          return item && item.state !== "DELETED";
        });
        setSelectedFileKeys(newSelectedFileKeys);
      }
    }, [selectedFileKeys, documentsTableStore.data, documentsTableStore.showDeleted]);

    const getNormalizedDropRowData = useCallback(
      (rowData: DocumentsTableItem) => ({
        type: rowData.type,
        id: rowData.id,
        state: rowData.state,
        fileMove: rowData.fileActions?.move,
        folderMove: rowData.folderActions?.move,
        collectionFolder: rowData.fileActions?.collectionFolder,
        dropTarget: rowData.state !== "DELETED" && !rowData.symlinkDirectory && !rowData.sourceProjectId,
      }),
      []
    );

    const renderDescription = useCallback((rowData: DocumentsTableItem) => {
      const isFolder = isFolderItem(rowData);
      const descriptionContent = rowData.description || "";
      const needsTooltip = descriptionContent.length >= 38;
      const isFileLabelsEnabled = projectsDataStore.functions?.includes("FILE_LABELS");
      const hasLabel = isFileLabelsEnabled && (isFolder ? rowData?.hasLabelRule : rowData?.labelFingerprint);

      return (
        <div className="text-ellipsis">
          {hasLabel &&
            (isFolder ? (
              <FontAwesomeIcon
                fixedWidth={true}
                icon={["fas", "tag"]}
                className="mr-sm file-state-icon"
                title={t("Has label rule")}
              />
            ) : (
              <Tooltip
                overlay={<LabelsPreview fetchPreview={() => fileDetailsStore.fetchLabelsPreview(rowData.id)} />}
                title={t("View labels")}
              >
                <FontAwesomeIcon fixedWidth={true} icon={["fas", "tag"]} className="mr-sm file-state-icon" />
              </Tooltip>
            ))}
          {needsTooltip ? (
            <Tooltip overlay={descriptionContent} title={descriptionContent}>
              {descriptionContent}
            </Tooltip>
          ) : (
            descriptionContent
          )}
        </div>
      );
    }, []);

    const selectedFiles = useMemo(() => {
      return selectedFileKeys
        .map((key) => {
          const id = fileIdFromRowKey(key);
          return id != null ? findFileById(documentsTableStore.data, id) : undefined;
        })
        .filter(Boolean);
    }, [selectedFileKeys, documentsTableStore.data]);

    const linkedMailToolTipText = useMemo(() => {
      return (
        <span>
          {t("This will open the ")}
          <strong>{t("original mail including attachments. ")}</strong>
          {t("You can use them to ")}
          <strong>{t("answer or forward ")}</strong>
          {t("the original mail.")}
        </span>
      );
    }, []);

    const isPrAdmin = projectsDataStore.isAdmin;
    const isRwmRight = documentsTableStore.isRWMAccessLevel();

    const columns: TableColumnProps<DocumentsTableItem>[] = useMemo(
      () => [
        {
          dataKey: "id",
          headerClassName: "invisible",
          width: 30,
          flexShrink: 0,
          cellRenderer: ({ rowData }: { rowData: DocumentsTableItem }) =>
            isFolderItem(rowData) ? (
              <FolderTypeIcon
                folderAccess={rowData.folderAccess}
                state={rowData.state}
                symlinkDirectory={rowData.symlinkDirectory}
                symlinkDirectorySource={rowData.symlinkDirectorySource}
                linkId={rowData.linkId}
              />
            ) : (
              <Tooltip
                overlay={
                  rowData.linkedMailId ? (
                    linkedMailToolTipText
                  ) : rowData.preview ? (
                    <FilePreview fileId={rowData.id} hasPreview={rowData.preview} />
                  ) : (
                    t("No preview available")
                  )
                }
                title={rowData.linkedMailId ? t("Open original mail") : rowData.preview ? t("Show preview") : ""}
                mouseEnterDelay={0.5}
              >
                <div
                  style={{ position: "relative", cursor: rowData.linkedMailId ? "pointer" : "default" }}
                  onClick={(e) => {
                    if (rowData.linkedMailId) {
                      e.stopPropagation();
                      goToLinkedMail(rowData.linkedMailId);
                    }
                  }}
                >
                  <FileTypeIcon
                    state={rowData.state}
                    relation={rowData.relations}
                    type={rowData.type}
                    size={rowData.size}
                    malware={rowData.malware}
                    linkedMailId={rowData.linkedMailId}
                    accessright={rowData.properties?.accessright}
                    fileActions={rowData.fileActions}
                  />
                </div>
              </Tooltip>
            ),
        },
        {
          dataKey: "name",
          label: t("Name"),
          flexGrow: 1,
          width: 140,
          cellRenderer: ({ cellData }) => (
            <Tooltip title={cellData} mouseEnterDelay={1}>
              <span className="text-ellipsis">{cellData}</span>
            </Tooltip>
          ),
        },
        {
          dataKey: "description",
          label: t("Description"),
          width: 250,
          cellRenderer: ({ rowData }) => renderDescription(rowData),
        },
        {
          dataKey: "index",
          label: t("Index"),
          width: 120,
        },
        {
          dataKey: "type",
          label: t("Type"),
          width: 70,
          flexShrink: 0,
          cellRenderer: ({ cellData, rowData }) =>
            cellData === "FOLDER" ? (
              <Tooltip title={isPrAdmin && rowData.defaultMetadata ? t("Default metadata configured") : ""}>
                <span className={classnames({ "with-asterisk": isPrAdmin && rowData.defaultMetadata })}>
                  {t("dir")}
                </span>
              </Tooltip>
            ) : (
              <span>
                {cellData}
                {rowData.pdfVersion && (
                  <Tooltip title={t("PDF version available")}>
                    <FontAwesomeIcon
                      fixedWidth={true}
                      icon={["far", "file-pdf"]}
                      className="file-pdf-version-icon"
                      transform="shrink-2 right-2 down-7"
                    />
                  </Tooltip>
                )}
              </span>
            ),
          filters: documentsTableStore.fileTypeFilters.map((item) => ({ text: item, value: item })),
        },
        {
          dataKey: "created",
          label: t("Last change"),
          width: 150,
          cellRenderer: ({ cellData }) => (cellData ? dayjs(cellData).format("lll") : null),
          initialSortOrder: "DESC",
        },
        {
          dataKey: "size",
          label: t("Size"),
          width: 80,
          cellRenderer: ({ cellData }) =>
            cellData != null && !isNaN(cellData) ? FormatData.formatBytes(cellData, 1) : null,
        },
        {
          dataKey: "id",
          label: t("Actions"),
          disableSort: true,
          width: 80,
          flexShrink: 0,
          cellRenderer: ({ rowData }) => (
            <Row justify="end" align="middle" wrap={false}>
              {isFileItem(rowData) && (rowData.fileActions?.download || rowData.fileActions?.downloadWithWatermark) && (
                <DirectDownload module="DOCUMENTS" fileId={rowData.id} />
              )}
              <DocumentsTableRowActions data={rowData} currentFolderId={documentsTableStore.currentFolderId}>
                <div className="docs-dropdown-trigger" onClick={(e) => e.stopPropagation()} title={t("More actions")}>
                  <FontAwesomeIcon className="email-action-icon" icon={["far", "ellipsis-v"]} fixedWidth={true} />
                </div>
              </DocumentsTableRowActions>
            </Row>
          ),
        },
        {
          headerClassName: "invisible",
          dataKey: "state",
          width: 12,
          style: { padding: 0 },
          cellRenderer: ({ rowData }) => (
            <div
              className={`status-marker marker-${rowData.fileState?.toString().toLowerCase() ?? "unknown"}`}
              title={rowData.fileState}
            />
          ),
        },
      ],
      [
        isPrAdmin,
        documentsTableStore.fileTypeFilters,
        linkedMailToolTipText,
        goToLinkedMail,
        renderDescription,
        documentsTableStore.currentFolderId,
      ]
    );

    return (
      <div className="documents-wrapper" data-testid="documents-page">
        <DocumentsSidebar onFolderSelect={documentsTableStore.goToFolder} />
        <div className="documents-main">
          <Row gutter={20} justify="end" className="mb-md" style={{ flexWrap: "nowrap" }} align="middle">
            <DocumentsSearchbar forbidEmptySearchString />
            {documentsTableStore.folderActions.fileUpload && (
              <Button
                style={{ marginLeft: 20 }}
                icon={<FontAwesomeIcon icon={["fas", "plus"]} />}
                type="primary"
                onClick={documentsTableStore.openFileUpload}
              >
                {t("New file")}
              </Button>
            )}

            <ToolsMenu
              showModal={showModal}
              folderActions={documentsTableStore.folderActions}
              folderMenuWorkflows={documentsTableStore.folderMenuWorkflows}
              folderId={documentsTableStore.currentFolderId}
            />

            <ViewLinkedMailModal />
          </Row>
          <Row justify="space-between" align="middle" className="mb-sm">
            <Row align="middle" gutter={8}>
              <DocumentsActionsMenu
                resetSelection={resetSelection}
                files={selectedFiles}
                showModal={showModal}
                currentFolderId={documentsTableStore.currentFolderId}
              />
              <RwmRightNotify visible={isRwmRight} />
            </Row>
            <div className="flex-center">
              <DeletedFilesSwitcher onChange={onShowDeletedChange} checked={documentsTableStore.showDeleted} />
            </div>
          </Row>
          <DocumentsBreadcrumbs
            goToFolder={documentsTableStore.goToFolder}
            config={breadcrumbsStore.data}
            enableCopyButton
          />
          <DocumentsDrop disabled={documentsTableStore.dndDisabled} uploadMode="DOCUMENTS">
            <ScrollableTable<DocumentsTableItem>
              ref={tableRef}
              stretchHeight={true}
              rowKey="_rowKey"
              sortBy={documentsTableStore.sortBy}
              sortDirection={documentsTableStore.sortOrder}
              columns={columns}
              rowCount={documentsTableStore.total}
              onSort={handleSort}
              onFilter={handleFilter}
              selectedRowKeys={selectedFileKeys}
              onSelectRows={handleSelectRows}
              onRowClick={handleRowClick}
              loading={documentsTableStore.loading}
              data={documentsTableStore.data}
              getCheckboxProps={getCheckboxProps}
              onCheckAllPredicate={onCheckAllPredicate}
              rowClassName={getRowClassName}
              highlightedRowId={currentHighlightedRows}
              draggableRows={true}
              normalizeDropRowData={getNormalizedDropRowData}
              getDraggableFromRowData={(rowData) =>
                !!(rowData.fileActions?.collectionFolder || rowData.fileActions?.move || rowData.folderActions?.move)
              }
              getDroppableFromRowData={(rowData) =>
                rowData.state !== "DELETED" &&
                isFolderItem(rowData) &&
                !rowData.symlinkDirectory &&
                !rowData.sourceProjectId
              }
              onDrop={handleDrop}
              resizeableHeaders={"DOCUMENTS"}
              ignoreResizeableHeaders={["id", "state", "size"]}
            />
          </DocumentsDrop>
        </div>
      </div>
    );
  })
);
